import {
  liveVoiceEventId,
  liveVoiceInputTranscriptEndMs,
  liveVoiceRecord,
  liveVoiceString,
  type LiveVoiceEvent,
  type LiveVoiceFailure,
} from './live-voice-protocol';

export type LiveVoiceFunctionCall = {
  delegationId: string;
  responseId: string;
  callId: string;
  name: string;
  arguments: string;
  /** Caption telemetry only. It does not authorize, deduplicate, or cancel work. */
  inputTranscriptEndMsAtDelegation: number | null;
};

type Batch = {
  delegationId: string;
  responseId: string;
  inputTranscriptEndMsAtDelegation: number | null;
  calls: LiveVoiceFunctionCall[];
  terminal: boolean;
};

type Options = {
  executeTool: (call: LiveVoiceFunctionCall) => Promise<string>;
  isActive: () => boolean;
  send: (event: LiveVoiceEvent) => void;
  onFailure?: (reason: LiveVoiceFailure) => void;
};

// Keep exact deduplication history for the entire session. Evicting an old ID
// would let a delayed retransmission repeat a side effect. These explicit client
// limits support over one response/second throughout the two-hour call lease.
export const LIVE_VOICE_FUNCTION_LIMITS = {
  delegations: 4096,
  responses: 8192,
  calls: 16384,
  pendingCalls: 256,
  pendingArgumentBytes: 2 * 1024 * 1024,
} as const;
const MAX_TOOL_BYTES = 64 * 1024;

/** Throw only when validation proves the application handler never started. */
export class LiveVoiceToolNotExecutedError extends Error {
  constructor(readonly reason: 'invalid_arguments') {
    super('Live voice tool was not executed');
    this.name = 'LiveVoiceToolNotExecutedError';
  }
}

/** Collect completed tool batches without inventing speech or turn events. */
export class LiveVoiceFunctionCalls {
  private inputTranscriptEndMs: number | null = null;
  private stopped = false;
  private readonly delegations = new Map<
    string,
    {
      inputTranscriptEndMsAtDelegation: number | null;
      responseId: string | null;
    }
  >();
  private readonly batches = new Map<string, Batch>();
  private readonly seenCallIds = new Set<string>();
  // Result/continuation commands have no success acknowledgment. Retain their
  // IDs until session end; their count is bounded by calls + responses above.
  private readonly criticalEventIds = new Set<string>();
  private pendingCalls = 0;
  private pendingArgumentBytes = 0;
  private pending = Promise.resolve();

  constructor(private readonly options: Options) {}

  get currentInputTranscriptEndMs(): number | null {
    return this.inputTranscriptEndMs;
  }

  observe(event: LiveVoiceEvent): void {
    if (this.stopped || !this.options.isActive()) return;
    const transcriptEndMs = liveVoiceInputTranscriptEndMs(event);
    if (transcriptEndMs !== null) {
      this.inputTranscriptEndMs = Math.max(
        this.inputTranscriptEndMs ?? 0,
        transcriptEndMs
      );
    }
    if (event.type === 'error') {
      const error = liveVoiceRecord(event.error);
      const rejectedEventId =
        liveVoiceString(error?.client_event_id) ??
        liveVoiceString(event.client_event_id);
      if (rejectedEventId && this.criticalEventIds.has(rejectedEventId))
        this.fail('protocol');
    } else if (event.type === 'session.delegation.created') {
      this.addDelegation(event);
    } else if (event.type === 'response.event') {
      this.addResponseEvent(event);
    }
  }

  whenIdle(): Promise<void> {
    return this.pending;
  }

  stop(): void {
    this.stopped = true;
    for (const batch of this.batches.values()) {
      this.releaseCalls(batch.calls);
      batch.calls = [];
    }
  }

  private fail(reason: LiveVoiceFailure): void {
    this.stop();
    this.options.onFailure?.(reason);
  }

  private addDelegation(event: LiveVoiceEvent): void {
    const value = liveVoiceRecord(event.delegation);
    const id = liveVoiceString(value?.id);
    if (!id || value?.target !== 'responses') return;
    if (this.delegations.has(id)) return;
    if (this.delegations.size >= LIVE_VOICE_FUNCTION_LIMITS.delegations)
      return this.fail('capacity');
    const responseId = liveVoiceString(value.response_id);
    this.delegations.set(id, {
      inputTranscriptEndMsAtDelegation: this.inputTranscriptEndMs,
      responseId,
    });
    if (responseId) this.addBatch(id, responseId);
  }

  private addBatch(delegationId: string, responseId: string): Batch | null {
    const existing = this.batches.get(responseId);
    if (existing) {
      if (existing.delegationId !== delegationId) this.fail('protocol');
      return existing.delegationId === delegationId ? existing : null;
    }
    const delegation = this.delegations.get(delegationId);
    if (!delegation) return null;
    if (this.batches.size >= LIVE_VOICE_FUNCTION_LIMITS.responses) {
      this.fail('capacity');
      return null;
    }
    const batch: Batch = {
      delegationId,
      responseId,
      inputTranscriptEndMsAtDelegation:
        delegation.inputTranscriptEndMsAtDelegation,
      calls: [],
      terminal: false,
    };
    this.batches.set(responseId, batch);
    return batch;
  }

  private addResponseEvent(envelope: LiveVoiceEvent): void {
    const delegationId = liveVoiceString(envelope.delegation_id);
    const inner = liveVoiceRecord(envelope.event);
    if (!delegationId || !inner) return;
    const delegation = this.delegations.get(delegationId);
    if (!delegation) return;
    const response = liveVoiceRecord(inner.response);
    const responseId = liveVoiceString(response?.id);
    if (inner.type === 'response.created' && responseId) {
      if (this.batches.get(responseId)?.terminal) return;
      this.addBatch(delegationId, responseId);
      delegation.responseId = responseId;
      return;
    }
    const batch = this.batches.get(responseId ?? delegation.responseId ?? '');
    if (!batch || batch.delegationId !== delegationId || batch.terminal) return;
    if (inner.type === 'response.output_item.done') {
      this.addCall(batch, inner.item);
    } else if (inner.type === 'response.completed') {
      if (!responseId || response?.status !== 'completed')
        return this.fail('protocol');
      batch.terminal = true;
      this.pending = this.pending
        .then(() => this.executeBatch(batch))
        .catch(() => this.fail('protocol'));
    } else if (
      inner.type === 'response.failed' ||
      inner.type === 'response.incomplete'
    ) {
      batch.terminal = true;
      this.releaseCalls(batch.calls);
      batch.calls = [];
    }
  }

  private addCall(batch: Batch, value: unknown): void {
    const item = liveVoiceRecord(value);
    if (item?.type !== 'function_call') return;
    const callId = liveVoiceString(item.call_id);
    const name = liveVoiceString(item.name);
    const args = liveVoiceString(item.arguments, MAX_TOOL_BYTES);
    if (!callId || !name || !args) return this.fail('protocol');
    const argumentBytes = new TextEncoder().encode(args).byteLength;
    if (argumentBytes > MAX_TOOL_BYTES) return this.fail('capacity');
    if (this.seenCallIds.has(callId)) return;
    if (
      this.seenCallIds.size >= LIVE_VOICE_FUNCTION_LIMITS.calls ||
      this.pendingCalls >= LIVE_VOICE_FUNCTION_LIMITS.pendingCalls ||
      this.pendingArgumentBytes + argumentBytes >
        LIVE_VOICE_FUNCTION_LIMITS.pendingArgumentBytes
    )
      return this.fail('capacity');
    this.seenCallIds.add(callId);
    this.pendingCalls++;
    this.pendingArgumentBytes += argumentBytes;
    batch.calls.push({
      delegationId: batch.delegationId,
      responseId: batch.responseId,
      inputTranscriptEndMsAtDelegation: batch.inputTranscriptEndMsAtDelegation,
      callId,
      name,
      arguments: args,
    });
  }

  private active(): boolean {
    return !this.stopped && this.options.isActive();
  }

  private releaseCalls(calls: LiveVoiceFunctionCall[]): void {
    this.pendingCalls -= calls.length;
    for (const call of calls)
      this.pendingArgumentBytes -= new TextEncoder().encode(
        call.arguments
      ).byteLength;
  }

  private sendCritical(event: LiveVoiceEvent): void {
    const eventId = liveVoiceEventId();
    this.criticalEventIds.add(eventId);
    this.options.send({ ...event, event_id: eventId });
  }

  private async executeBatch(batch: Batch): Promise<void> {
    const calls = batch.calls;
    batch.calls = [];
    if (!calls.length) return;
    try {
      for (const call of calls) {
        if (!this.active()) return;
        let output: string;
        try {
          output = await this.options.executeTool(call);
        } catch (error) {
          output = JSON.stringify(
            error instanceof LiveVoiceToolNotExecutedError
              ? {
                  status: 'not_executed',
                  reason: error.reason,
                  operationMayHaveCompleted: false,
                }
              : {
                  status: 'outcome_unknown',
                  reason: 'tool_execution_failed',
                  operationMayHaveCompleted: true,
                }
          );
        }
        if (!this.active()) return;
        if (
          typeof output !== 'string' ||
          new TextEncoder().encode(output).byteLength > MAX_TOOL_BYTES
        ) {
          output = JSON.stringify({
            status: 'result_unavailable',
            reason: 'invalid_tool_result',
            operationMayHaveCompleted: true,
          });
        }
        this.sendCritical({
          type: 'response.item.create',
          item: { type: 'function_call_output', call_id: call.callId, output },
        });
      }
      if (this.active()) {
        this.sendCritical({
          type: 'response.create',
        });
      }
    } finally {
      this.releaseCalls(calls);
    }
  }
}
